import { useRouter } from 'next/router';
import { serverSideTranslations } from 'next-i18next/serverSideTranslations';
import OpenGraph from '@/components/OpenGraph';
import Fallback from '@/components/Fallback';
import HouseDetailContainer from '@/src/components/UI/HouseDetail/HouseDetailContainer';
import { getOgDescription } from '@/src/libs/og-helper';
import { useJWT } from '@/lib/useJWT';
import { GetStaticPaths, GetStaticProps } from 'next';
import { isNotSet } from '@/utils/formatChecker';
import FunwooAPI, { Agent, BuildingShowcaseDetailsEntity, ListingForBuy } from '@/swagger';
import serializer from '@/src/libs/serializer';
import { AxiosError } from 'axios';
import { GetStaticPathsResult } from 'next/types';
import { useMemo } from 'react';

interface Props extends ListingForBuy {
  building: BuildingShowcaseDetailsEntity | null;
}

const HouseItemDetail = ({ listing, agents, agentLookup, building }: Props) => {
  const router = useRouter();
  if (router.isFallback) {
    return <Fallback />;
  }
  const session = useJWT();
  const loginAgentSID = agentLookup[session?.email || ''];
  // custom listing URL with login agent SID
  const listingUrl = loginAgentSID
    ? `${process.env.NEXT_PUBLIC_HOSTNAME}/buy/${listing.sid}/${loginAgentSID}`
    : `${process.env.NEXT_PUBLIC_HOSTNAME}/buy/${listing.sid}`;

  // needs to figure out why ConcatArray<string> doesn't accept string as argument
  const keywords = ([] as string[])
    .concat(
      listing.title,
      listing.display_building_project ? listing?.building_project || '' : '',
      listing.feature_tags || []
    )
    .filter(Boolean);

  const displayAgent = useMemo(() => {
    let defaultAgent = agents[0];

    if (!defaultAgent.active) {
      defaultAgent = agents.find((agent) => agent.id === listing?.agent1_id) ?? agents[0];
    }
    return defaultAgent;
  }, [agents, listing]);

  return (
    <>
      <OpenGraph
        title={`${listing.title}`}
        description={getOgDescription(serializer(listing))}
        type='funwoo.listing'
        url={`${listingUrl}/${displayAgent.sid}`}
        imageUrl={listing?.listing_image?.[0]?.watermark_image || ''}
        keywords={keywords}
      />
      <HouseDetailContainer
        detailData={serializer(listing)}
        agent={displayAgent}
        listingUrl={listingUrl}
        building={building}
      />
    </>
  );
};

// noinspection JSUnusedGlobalSymbols
export const getStaticPaths: GetStaticPaths<{ entity: Array<string> }> = async () => {
  const [{ data: listings }, { data: agents }] = await Promise.all([
    FunwooAPI.listingApi.findAll({ onlyActivate: true }),
    FunwooAPI.agentApi.findAll(),
  ]);

  const sorted = listings.reduce<Array<{ listingSid: string; availableAgents: Array<Agent> }>>((prev, curr) => {
    const country = curr.country!;
    const availableAgents: Array<Agent> = agents.filter((agent) => {
      const serveLocation = (agent.agent_service_location ?? []).map((location) => location.location);
      return serveLocation.some((location) => (location ?? '').startsWith(country));
    });
    return prev.concat({ listingSid: curr.sid, availableAgents });
  }, []);

  let paths: GetStaticPathsResult<{ entity: Array<string> }>['paths'] = [];

  sorted.forEach(({ listingSid, availableAgents }) => {
    availableAgents.forEach((agent) => {
      paths.push({
        params: {
          entity: [listingSid, agent.sid],
        },
      });
    });
  });

  return {
    paths,
    fallback: 'blocking',
  };
};

export const getStaticProps: GetStaticProps<
  Props,
  {
    entity: Array<string>;
  },
  {}
> = async ({ locale = '', params: { entity } = { entity: [] } }) => {
  try {
    const [listingSid, agentSid = ''] = entity;
    let building: BuildingShowcaseDetailsEntity | null = null;

    const { data, status } = await FunwooAPI.listingApi
      .findOneForBuy({
        listingSid,
        agentSid,
      })
      .catch((error: AxiosError) => {
        if (error.response?.status === 409) return { data: null, status: 409 };
        else throw error;
      });
    const buildingId = data?.listing.buildingId;
    if (buildingId) building = await FunwooAPI.buildingApi.getShowcaseDetailsById(buildingId).then((res) => res.data);

    if (status === 203) {
      return {
        props: {
          ...(await serverSideTranslations(locale, ['common'])),
        },
        redirect: {
          destination: `/buy/inactive/${listingSid}`,
          permanent: true,
        },
      };
    }

    if (isNotSet(data)) {
      return {
        props: {
          ...(await serverSideTranslations(locale, ['common'])),
        },
        notFound: true,
        revalidate: Number(process.env.NEXT_PUBLIC_REVALIDATE_INTERVAL),
      };
    }

    return {
      props: {
        ...(await serverSideTranslations(locale, ['common'])),
        ...data,
        building,
      },
      revalidate: Number(process.env.NEXT_PUBLIC_REVALIDATE_INTERVAL),
    };
  } catch (e) {
    return {
      props: {
        ...(await serverSideTranslations(locale, ['common'])),
      },
      notFound: true,
    };
  }
};

// noinspection JSUnusedGlobalSymbols
export default HouseItemDetail;
